import type * as React from 'react';
import { cn } from '../../lib/cn.ts';
import type { ButtonType } from '../../lib/buttonType.ts';

/**
 * How wide the history column stands, and the width below which the whole
 * viewport is too narrow to give it up.
 *
 * The second number is not the mobile breakpoint: this is about whether there
 * is room for two columns beside the rail, which happens well before a window
 * is a phone.
 */
export const CHAT_SIDEBAR_WIDTH = 272;

export type ChatSidebarProps = Omit<React.ComponentProps<'div'>, 'style'>;

/**
 * The earlier conversations, standing beside the one being had.
 *
 * Only in full screen, and only there because that is the one presentation with
 * room for it — a 400px working column spent on a list is a column not spent on
 * the conversation. It is always open rather than behind a button: a list you
 * have to ask for is a list nobody remembers is there.
 *
 * Below 760px of window it goes entirely. Two columns in less than that leaves
 * neither of them readable, and a history nobody can read is worse than one
 * that waits for a wider window.
 */
export function ChatSidebar({ className, ...props }: ChatSidebarProps) {
  return (
    <div
      data-slot='chat-sidebar'
      className={cn(
        'flex w-68 shrink-0 flex-col overflow-y-auto',
        'border-r border-line bg-surface/50 px-3 py-4',
        'max-[760px]:hidden',
        className,
      )}
      {...props}
    />
  );
}

export type ChatSidebarNewProps = Omit<
  React.ComponentProps<'button'>,
  'style' | 'type'
> & {
  type?: ButtonType;
};

/**
 * Starting another conversation, at the top of the list of the ones there are.
 *
 * The navy pill the rest of the app uses for "make a new thing", narrowed to
 * the column it stands in — this creates a sitting, so it should not look like
 * anything else.
 */
export function ChatSidebarNew({
  className,
  type = 'button',
  ...props
}: ChatSidebarNewProps) {
  return (
    <button
      data-slot='chat-sidebar-new'
      type={type}
      className={cn(
        'mb-3.5 flex w-full shrink-0 cursor-pointer items-center gap-2 rounded-full',
        'bg-primary py-2.5 pr-4.5 pl-3.5 text-left text-base font-bold text-white',
        'transition hover:bg-primary-hover',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
        '[&_svg]:size-3.5 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

export type ChatSidebarGroupProps = Omit<React.ComponentProps<'h3'>, 'style'>;

/** When the conversations under it were had — "This week", "Earlier". */
export function ChatSidebarGroup({ className, ...props }: ChatSidebarGroupProps) {
  return (
    <h3
      data-slot='chat-sidebar-group'
      className={cn(
        'px-2.5 pt-2 pb-1 text-xs font-semibold tracking-wide text-ink-muted uppercase',
        className,
      )}
      {...props}
    />
  );
}

export type ChatSidebarListProps = Omit<React.ComponentProps<'ul'>, 'style'>;

/** The conversations in one group, most recently spoken in first. */
export function ChatSidebarList({ className, ...props }: ChatSidebarListProps) {
  return (
    <ul
      data-slot='chat-sidebar-list'
      className={cn('flex flex-col', className)}
      {...props}
    />
  );
}

export interface ChatSidebarItemProps extends Omit<
  React.ComponentProps<'button'>,
  'style' | 'children' | 'type'
> {
  type?: ButtonType;
  /** The conversation's opening line — what tells it from the others. */
  preview: string;
  /** When it was last spoken in, already written the way it should read. */
  when: string;
  /** The sitting the drawer is showing. Marked, not just tinted. */
  current?: boolean;
  /** A short mark at the end of the first line, such as a count. */
  badge?: React.ReactNode;
  /** How many lines the heading may take; a list of names wants one, cut with an ellipsis. */
  previewLines?: 1 | 2;
}

/**
 * One earlier conversation.
 *
 * The opening line is the heading and the date is detail, because that is the
 * order somebody scanning the list reads them in. Two lines at most: a list
 * item that wraps to four is a list nobody scans.
 */
export function ChatSidebarItem({
  preview,
  when,
  current = false,
  badge,
  previewLines = 2,
  className,
  type = 'button',
  ...props
}: ChatSidebarItemProps) {
  return (
    <li>
      <button
        data-slot='chat-sidebar-item'
        data-current={current}
        /* Said as well as tinted: a background alone is not a state anyone hears. */
        aria-current={current ? true : undefined}
        type={type}
        className={cn(
          'w-full cursor-pointer rounded-sm px-2.5 py-2.25 text-left transition',
          'hover:bg-fill',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
          current && 'bg-fill',
          className,
        )}
        {...props}
      >
        <span className='flex min-w-0 items-center gap-2'>
          <span
            className={cn(
              'min-w-0 flex-1 text-base font-medium text-ink',
              previewLines === 1 ? 'truncate' : 'line-clamp-2',
            )}
          >
            {preview}
          </span>
          {badge}
        </span>
        <span className='mt-0.5 block truncate text-xs text-ink-muted'>{when}</span>
      </button>
    </li>
  );
}

export type ChatSidebarEmptyProps = Omit<React.ComponentProps<'p'>, 'style'>;

export function ChatSidebarEmpty({ className, ...props }: ChatSidebarEmptyProps) {
  return (
    <p
      data-slot='chat-sidebar-empty'
      className={cn('px-2.5 py-5 text-sm text-ink-muted', className)}
      {...props}
    />
  );
}
